import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { Newspaper, MapPin, CalendarDays, ArrowLeft, ArrowRight, ExternalLink, ListChecks, CheckCircle2, MapPinned, HelpCircle } from "lucide-react";
import ShareButton from "@/components/ShareButton";
import FbGroupShare from "@/components/FbGroupShare";
import FaqJsonLd from "@/components/FaqJsonLd";
import FeaturedPartners from "@/components/FeaturedPartners";
import PressOffers from "@/components/PressOffers";
import PressReactions from "@/components/PressReactions";
import LocalTopDeal from "@/components/LocalTopDeal";

// Bloc article au format identique à la page actu (résumé, essentiel, pourquoi, source, partage, réactions, bon plan, FAQ).
function ArticleBlock({ post, idx, shareUrl }) {
  const summary = post.excerpt || post.meta_description || "";
  const track = (ch) => { api.post(`/press-review/${post.slug}/share`, { channel: ch }).catch(() => {}); };
  return (
    <div data-testid={`trio-article-${idx}`} className={idx > 0 ? "mt-10 pt-8 border-t-2 border-slate-100" : ""}>
      <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
        <span className="px-2 py-0.5 rounded-full bg-[#172A3A] text-white font-semibold">Actu locale</span>
        {post.city && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {post.city}</span>}
        {post.published_at && <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {new Date(post.published_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span>}
      </div>
      <h2 className="mt-2 font-heading text-2xl sm:text-3xl font-extrabold text-slate-900" data-testid={`trio-title-${idx}`}>{post.title}</h2>
      {summary && <p className="mt-3 text-lg leading-relaxed text-slate-700">{summary}</p>}

      {post.key_points?.length > 0 && (
        <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-5">
          <div className="inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-[#172A3A]"><ListChecks size={16} className="text-[#E05638]" /> L'essentiel en bref</div>
          <ul className="mt-3 space-y-2">
            {post.key_points.map((k, i) => (
              <li key={i} className="flex items-start gap-2 text-slate-700"><CheckCircle2 size={18} className="text-[#E05638] shrink-0 mt-0.5" /> <span>{k}</span></li>
            ))}
          </ul>
        </div>
      )}
      {post.local_angle && (
        <div className="mt-5 rounded-2xl border-l-4 border-[#E05638] bg-orange-50/50 p-5">
          <div className="inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-[#E05638]"><MapPinned size={16} /> Pourquoi ça compte près de chez vous</div>
          <p className="mt-2 text-slate-700 leading-relaxed">{post.local_angle}</p>
        </div>
      )}

      {post.source_url && (
        <div className="mt-5 rounded-2xl border border-orange-200 bg-orange-50/60 p-5">
          <div className="text-sm text-slate-600">Résumé proposé par <span className="font-semibold text-slate-800">PROXIreduc-28</span>. L'article complet est à lire sur le site du média.</div>
          <a href={post.source_url} target="_blank" rel="noopener noreferrer" onClick={() => track("source_click")}
            className="mt-3 inline-flex items-center gap-2 rounded-xl bg-[#E05638] hover:bg-[#c94a2f] text-white font-bold px-6 py-3 text-base transition-colors">
            Lire l'article complet sur {post.source_name || "le média"} <ExternalLink size={18} className="opacity-80" />
          </a>
        </div>
      )}

      {/* Partage + dérouleur groupes sur la MÊME ligne */}
      <div className="mt-5 flex flex-wrap items-center gap-2" data-testid={`trio-share-row-${idx}`}>
        <span className="text-sm font-bold text-slate-700 mr-1">Partager</span>
        <ShareButton url={shareUrl} title={post.title} text={post.title} testId={`trio-share-btn-${idx}`} onShare={track} />
        <div className="flex-1 min-w-[220px] [&>div]:mt-0">
          <FbGroupShare small showTopCopy testId={`trio-groups-${idx}`} text={post.title} shareUrl={shareUrl} cityHint={post.city || ""} onShared={() => track("fb_groups")} />
        </div>
      </div>

      <PressReactions slug={post.slug} initial={post.reactions} testId={`trio-reactions-${idx}`} />
      <LocalTopDeal city={post.city || ""} testId={`trio-top-deal-${idx}`} />

      {post.faq?.length > 0 && (
        <div className="mt-8 pt-6 border-t border-slate-100" data-testid={`trio-faq-${idx}`}>
          <h3 className="font-heading text-xl font-bold text-[#172A3A] inline-flex items-center gap-2"><HelpCircle size={20} className="text-[#E05638]" /> Questions fréquentes</h3>
          <div className="mt-4 space-y-3">
            {post.faq.map((f, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="font-semibold text-slate-900">{f.q}</div>
                <p className="mt-1 text-sm text-slate-600">{f.a}</p>
              </div>
            ))}
          </div>
          <FaqJsonLd faq={post.faq} />
        </div>
      )}
    </div>
  );
}

export default function PressTrio() {
  const { slug } = useParams();
  const [trio, setTrio] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setTrio(null); setNotFound(false);
    api.get(`/press-trio/${slug}`).then((r) => setTrio(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useSeo({
    title: trio ? `${trio.title} | PROXIreduc-28` : "Revue de presse | PROXIreduc-28",
    description: trio ? (trio.titles || []).join(" · ") : "",
    canonicalPath: `/actu-3/${slug}`,
    image: trio ? (trio.triptych_image || "/og-blog.jpg") : "/og-blog.jpg",
    type: "article",
  });

  if (notFound) return (
    <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="press-trio-404">
      <h1 className="font-heading text-2xl font-bold">Revue introuvable</h1>
      <Link to="/actu" className="mt-4 inline-flex items-center gap-1 text-[#E05638] font-semibold"><ArrowLeft size={16} /> Retour à l'actu locale</Link>
    </div>
  );
  if (!trio) return <div className="max-w-2xl mx-auto px-4 py-20 text-slate-500">Chargement…</div>;

  const vitrineUrl = `${process.env.REACT_APP_BACKEND_URL}/api/actu-3-vitrine/${slug}`;
  const arts = trio.articles || [];

  return (
    <article className="pb-24 sm:pb-16" data-testid="press-trio">
      {trio.triptych_image && (
        <div className="w-full aspect-[2.4/1] max-h-[460px] bg-slate-100 overflow-hidden">
          <img src={trio.triptych_image} alt={trio.title} className="w-full h-full object-cover" data-testid="press-trio-cover" />
        </div>
      )}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/actu" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#E05638] bg-orange-50 hover:bg-orange-100 border border-orange-200 px-3.5 py-2 rounded-full transition-colors"><ArrowLeft size={15} /> Toute l'actu locale</Link>
        <h1 className="mt-4 font-heading text-3xl sm:text-4xl font-extrabold text-slate-900" data-testid="press-trio-heading">{trio.title}</h1>

        {arts[0] && <ArticleBlock post={arts[0]} idx={0} shareUrl={vitrineUrl} />}
        {arts[1] && <ArticleBlock post={arts[1]} idx={1} shareUrl={vitrineUrl} />}

        {/* Partenaires à l'honneur (4) — entre l'article 2 et l'article 3 */}
        <div className="mt-10"><FeaturedPartners limit={4} testId="press-trio-featured-partners" track city={arts[0]?.city || ""} /></div>

        {arts[2] && <ArticleBlock post={arts[2]} idx={2} shareUrl={vitrineUrl} />}

        <PressOffers limit={4} testId="press-trio-press-offers" track city={arts[0]?.city || ""} />

        <div className="mt-10 text-center">
          <Link to="/actu" className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-[#E05638] hover:bg-[#c94a2f] px-5 py-2.5 rounded-xl transition-colors"><ArrowLeft size={16} /> Toute l'actu locale</Link>
        </div>
      </div>
    </article>
  );
}
